import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
} from "react";

interface LoadingState {
  isLoading: boolean;
  loadingStates: Map<string, boolean>;
  loadingMessages: Map<string, string>;
  loadingTimestamps: Map<string, number>;
}

type LoadingAction =
  | { type: "START_LOADING"; id: string; message?: string }
  | { type: "STOP_LOADING"; id: string }
  | { type: "RESET" }
  | { type: "CLEANUP_STALE" };

interface LoadingContextType extends LoadingState {
  startLoading: (id: string, message?: string) => void;
  stopLoading: (id: string) => void;
  resetLoading: () => void;
  getLoadingDuration: (id: string) => number | null;
}

const LOADING_TIMEOUT = 30000; // 30 seconds
const DEBUG = process.env.NODE_ENV === "development";

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

function logDebug(message: string, data?: any) {
  if (DEBUG) {
    console.debug(`[LoadingContext] ${message}`, data);
  }
}

function loadingReducer(
  state: LoadingState,
  action: LoadingAction,
): LoadingState {
  switch (action.type) {
    case "START_LOADING": {
      logDebug("Starting loading state:", {
        id: action.id,
        message: action.message,
      });

      const newLoadingStates = new Map(state.loadingStates);
      newLoadingStates.set(action.id, true);

      const newLoadingMessages = new Map(state.loadingMessages);
      if (action.message) {
        newLoadingMessages.set(action.id, action.message);
      }

      const newLoadingTimestamps = new Map(state.loadingTimestamps);
      newLoadingTimestamps.set(action.id, Date.now());

      const newState = {
        isLoading: true,
        loadingStates: newLoadingStates,
        loadingMessages: newLoadingMessages,
        loadingTimestamps: newLoadingTimestamps,
      };

      logDebug("New loading state:", newState);
      return newState;
    }

    case "STOP_LOADING": {
      logDebug("Stopping loading state:", { id: action.id });

      const newLoadingStates = new Map(state.loadingStates);
      const wasLoading = newLoadingStates.delete(action.id);

      const newLoadingMessages = new Map(state.loadingMessages);
      newLoadingMessages.delete(action.id);

      const newLoadingTimestamps = new Map(state.loadingTimestamps);
      const startTime = newLoadingTimestamps.get(action.id);
      newLoadingTimestamps.delete(action.id);

      if (wasLoading && startTime) {
        const duration = Date.now() - startTime;
        logDebug(`Loading operation completed in ${duration}ms`, {
          id: action.id,
        });
      }

      const newState = {
        isLoading: newLoadingStates.size > 0,
        loadingStates: newLoadingStates,
        loadingMessages: newLoadingMessages,
        loadingTimestamps: newLoadingTimestamps,
      };

      logDebug("New loading state:", newState);
      return newState;
    }

    case "CLEANUP_STALE": {
      const now = Date.now();
      const newLoadingStates = new Map(state.loadingStates);
      const newLoadingMessages = new Map(state.loadingMessages);
      const newLoadingTimestamps = new Map(state.loadingTimestamps);
      let staleStatesFound = false;

      // Use Array.from to convert Map entries to an array for iteration
      Array.from(state.loadingTimestamps.entries()).forEach(
        ([id, timestamp]) => {
          if (now - timestamp > LOADING_TIMEOUT) {
            logDebug("Cleaning up stale loading state:", {
              id,
              duration: now - timestamp,
            });
            newLoadingStates.delete(id);
            newLoadingMessages.delete(id);
            newLoadingTimestamps.delete(id);
            staleStatesFound = true;
          }
        },
      );

      if (!staleStatesFound) {
        return state;
      }

      const newState = {
        isLoading: newLoadingStates.size > 0,
        loadingStates: newLoadingStates,
        loadingMessages: newLoadingMessages,
        loadingTimestamps: newLoadingTimestamps,
      };

      logDebug("New loading state after cleanup:", newState);
      return newState;
    }

    case "RESET": {
      logDebug("Resetting all loading states");
      return {
        isLoading: false,
        loadingStates: new Map(),
        loadingMessages: new Map(),
        loadingTimestamps: new Map(),
      };
    }

    default:
      return state;
  }
}

export function LoadingProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(loadingReducer, {
    isLoading: false,
    loadingStates: new Map(),
    loadingMessages: new Map(),
    loadingTimestamps: new Map(),
  });

  const cleanupInterval = useRef<NodeJS.Timeout | undefined>(undefined);

  // Set up cleanup interval
  useEffect(() => {
    cleanupInterval.current = setInterval(() => {
      dispatch({ type: "CLEANUP_STALE" });
    }, LOADING_TIMEOUT / 2);

    return () => {
      if (cleanupInterval.current) {
        clearInterval(cleanupInterval.current);
      }
    };
  }, []);

  const startLoading = useCallback((id: string, message?: string) => {
    dispatch({ type: "START_LOADING", id, message });
  }, []);

  const stopLoading = useCallback((id: string) => {
    dispatch({ type: "STOP_LOADING", id });
  }, []);

  const resetLoading = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  const getLoadingDuration = useCallback(
    (id: string): number | null => {
      const startTime = state.loadingTimestamps.get(id);
      if (!startTime) return null;
      return Date.now() - startTime;
    },
    [state.loadingTimestamps],
  );

  return (
    <LoadingContext.Provider
      value={{
        ...state,
        startLoading,
        stopLoading,
        resetLoading,
        getLoadingDuration,
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
}

// Enhanced hook for automatic loading state management with timeout
export function useLoadingState(
  id: string,
  message?: string,
  timeout?: number,
) {
  const { startLoading, stopLoading, getLoadingDuration } = useLoading();
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const start = useCallback(() => {
    startLoading(id, message);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout if specified
    if (timeout) {
      timeoutRef.current = setTimeout(() => {
        const duration = getLoadingDuration(id);
        logDebug(`Loading state timed out after ${duration}ms`, {
          id,
          message,
        });
        stop();
      }, timeout);
    }
  }, [id, message, timeout, startLoading, getLoadingDuration]);

  const stop = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    stopLoading(id);
  }, [id, stopLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Clean up loading state if component unmounts while loading
      if (getLoadingDuration(id) !== null) {
        logDebug("Cleaning up loading state on unmount:", { id });
        stopLoading(id);
      }
    };
  }, [id, stopLoading, getLoadingDuration]);

  return { start, stop };
}
